import { IconTile } from "@cmp-src/data-display/IconTile";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { SelectItem, UnifiedSelect } from "@cmp-src/form/UnifiedSelect";
import { type IconValue } from "@cmp-src/icons";
import { cn } from "@gettactic/helpers";
import { useMemo } from "react";
import { classes } from "./utils";

interface RequestSelectOption {
  id: string;
  name: string;
  email?: string;
  profile_photo_url?: string;
  /** When set, the item renders a colored IconTile (used by the type picker). */
  icon?: IconValue | null;
  /** Background color (hex) for the IconTile; pairs with `icon`. */
  tileColor?: string;
  /** When set, the item renders a colored dot (used by the priority picker). */
  dotClass?: string;
}

interface RequestSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  triggerContent: React.ReactNode;
  options: RequestSelectOption[];
  placeholder?: string;
  className?: string;
}

export const RequestSelect = ({
  value,
  onValueChange,
  disabled = false,
  triggerContent,
  options,
  placeholder = "Select an option",
  className
}: RequestSelectProps) => {
  // Convert options to SelectItem format
  const items = useMemo<SelectItem[]>(
    () =>
      options.map((option) => ({
        id: option.id,
        name: option.name,
        keywords: [option.name, option.email || ""],
        data: option
      })),
    [options]
  );

  // Find selected item
  const selectedItem = useMemo(
    () => items.find((item) => item.id === value) || null,
    [items, value]
  );

  return (
    <UnifiedSelect
      items={items}
      value={selectedItem}
      onSelect={(item) => onValueChange(item?.id || "")}
      disabled={disabled}
      placeholder={placeholder}
      trigger={
        <button
          type="button"
          className={cn(
            classes.selectTrigger,
            "w-full px-3 text-left",
            className
          )}
        >
          {triggerContent}
        </button>
      }
      renderItem={(item) => {
        const option = item.data as RequestSelectOption;
        return (
          <div className="flex items-center space-x-2">
            {Object.hasOwn(option, "profile_photo_url") ? (
              <UserAvatar
                user={{
                  name: option.id === "no-owner" ? "?" : option.name || "?",
                  email: option.email || "",
                  profile_photo_url: option.profile_photo_url || ""
                }}
                size="sm"
                className="flex-shrink-0"
              />
            ) : option.id === "no-owner" ? (
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-neutral-500">
                <span className="font-medium text-white text-xs">NA</span>
              </div>
            ) : Object.hasOwn(option, "tileColor") ? (
              <IconTile
                color={option.tileColor}
                icon={option.icon ?? null}
                size="sm"
                className="h-6 w-6 rounded-[6px]"
              />
            ) : Object.hasOwn(option, "dotClass") ? (
              <span
                className={cn(
                  "h-2 w-2 flex-shrink-0 rounded-full",
                  option.dotClass
                )}
              />
            ) : null}
            <span className="truncate">{option.name}</span>
          </div>
        );
      }}
    />
  );
};
